import { setGenerateMediaPlanData } from "@/features/gridDataSlice";
import {
    IValueBoolean,
    PlanMetrics,
    TMediaPlanEditConfig,
    TMediaPlanRowData,
} from "@/ts/common-types";
import {
    ABSOLUTE_PLAN_METRICES,
    GENERATE_MEDIA_PLAN_CONFIG,
} from "@/ts/constants";
import {
    getAbsoluteMetricsToAdjust,
    getOverallAdjustedPlanData,
    getEditedPlanLevelConfig,
    numericValueFormatter,
    saveMediaPlanEditConfig,
    getUpdatedRatioMetrics,
} from "@/ts/utils";
import { RATIOS } from "@/workers/row-aggregator.worker";
import { ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { Input, InputRef, Modal } from "antd";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const RATIO_METRIC_MAPPING = {
    cpm: PlanMetrics.IMPRESSION,
    ctr: PlanMetrics.CLICK,
    cpc: PlanMetrics.CLICK,
    cr: PlanMetrics.ORDERS,
    aov: PlanMetrics.SALE,
};

const ABSOLUTE_METRIC_ADJUST_CONFIG = {
    click: {
        metric: PlanMetrics.IMPRESSION,
        ratio: PlanMetrics.CTR,
    },
    orders: {
        metric: PlanMetrics.CLICK,
        ratio: PlanMetrics.CR,
    },
    sale: {
        metric: PlanMetrics.ORDERS,
        ratio: PlanMetrics.AOV,
    },
};

const PlanRatioCellRenderer = (params: ICellRendererParams) => {
    const dispatch = useDispatch();
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(params.value);
    const [originalValue, setOriginalValue] = useState(params.value);

    const inputRef = useRef<InputRef>(null);
    const cellRef = useRef<HTMLDivElement>(null);

    const mediaPlanEditConfig = useSelector(
        (state: { mediaPlanEditConfig: { value: TMediaPlanEditConfig } }) =>
            state.mediaPlanEditConfig.value
    );

    const mediaPlanData = useSelector(
        (state: { generateMediaPlanData: { value: TMediaPlanRowData } }) =>
            state.generateMediaPlanData.value
    );

    const isMediaPlanEdit = useSelector(
        (state: { isMediaPlanEdit: IValueBoolean }) =>
            state.isMediaPlanEdit.value
    );

    const isMediaPlanCreate = useSelector(
        (state: { isMediaPlanCreate: IValueBoolean }) =>
            state.isMediaPlanCreate.value
    );

    const handleDoubleClick = () => {
        setIsEditing(true);
        setOriginalValue(params.value);
        setInputValue(params.value);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const showErrorModal = (errorMessage?: string) => {
        Modal.error({
            title: "Error",
            className: "plan-confirm-modal",
            content: errorMessage || "Something went wrong. Please try again.",
            okText: "Close",
        });
    };

    const handleInputSubmit = () => {
        setIsEditing(false);
        if (inputValue !== originalValue) {
            const {
                editedPlanLevel,
                editedPlanRecordKey,
                editedPlanRecord,
                editPlanRecordData,
            } = getEditedPlanLevelConfig(params);

            const ratioField = params.colDef
                .field as keyof typeof RATIO_METRIC_MAPPING;
            const ratio = RATIOS[ratioField];
            const targetMetric = RATIO_METRIC_MAPPING[ratioField];
            const otherMetricValue =
                params.data[
                ratio.denominator === targetMetric
                    ? ratio.numerator
                    : ratio.denominator
                ];
            let newAbsoluteValue;
            if (ratio.denominator === targetMetric) {
                // If target is denominator: other_metric / (ratio / multiplier)
                newAbsoluteValue =
                    otherMetricValue / (inputValue / (ratio.multiplier || 1));
            } else {
                // If target is numerator: other_metric * (ratio / multiplier)
                newAbsoluteValue =
                    otherMetricValue * (inputValue / (ratio.multiplier || 1));
            }

            const modifiedRecord = {
                [targetMetric]: newAbsoluteValue,
                [ratioField]: inputValue,
            };

            const startIndex = ABSOLUTE_PLAN_METRICES.indexOf(targetMetric);
            if (startIndex === -1) return;

            for (
                let i = startIndex + 1;
                i < ABSOLUTE_PLAN_METRICES.length;
                i++
            ) {
                const currentMetric = ABSOLUTE_PLAN_METRICES[i];
                const config =
                    ABSOLUTE_METRIC_ADJUST_CONFIG[
                    currentMetric as keyof typeof ABSOLUTE_METRIC_ADJUST_CONFIG
                    ];

                // Get the source metric value and ratio
                const sourceMetricValue = modifiedRecord[config.metric];
                const ratioValue = params.data[config.ratio];

                // Calculate new value based on the configuration
                let newValue;
                if (config.ratio === "aov") {
                    // AOV is a direct multiplier
                    newValue = sourceMetricValue * ratioValue;
                } else {
                    // Other ratios are percentages
                    newValue = sourceMetricValue * (ratioValue / 100);
                }

                modifiedRecord[currentMetric] = newValue;
            }

            const updatedRatioMetrics = getUpdatedRatioMetrics(
                editPlanRecordData,
                {
                    ...editPlanRecordData,
                    ...modifiedRecord,
                }
            )

            const updatedRecord = {
                ...editPlanRecordData,
                ...modifiedRecord,
                ...updatedRatioMetrics,
                isRowModified: true,
            };

            const absoluteMetricesToAdjust = getAbsoluteMetricsToAdjust(
                editPlanRecordData,
                updatedRecord,
                editPlanRecordData.spend
            );

            const { adjustmentStatus, errMessage, planData } =
                getOverallAdjustedPlanData({
                    mediaPlanData,
                    editedPlanLevel,
                    editedPlanRecordKey,
                    editedPlanRecord,
                    modifiedRecord,
                    finalUpdatedRecord: updatedRecord,
                    absoluteMetricesToAdjust,
                    isRatioEdit: true,
                });

            if (!adjustmentStatus) {
                showErrorModal(`Plan adjustment failed  - ${errMessage!}`);
                params.node.setDataValue(PlanMetrics.SPEND, originalValue);
            } else {
                saveMediaPlanEditConfig(
                    editedPlanLevel,
                    editedPlanRecord,
                    params
                );

                dispatch(setGenerateMediaPlanData(planData));
            }
        }
    };

    const handleInputBlur = () => {
        handleInputSubmit();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleInputSubmit();
        } else if (e.key === "Escape") {
            setInputValue(originalValue);
            setIsEditing(false);
            params.node.setDataValue(params.colDef.field, originalValue);
        }
    };

    const isModifiedCell = () => {
        const planLevel = params.columnApi
            .getAllColumns()[0]
            .getColDef().headerName;

        const editedPlanRecordKey =
            GENERATE_MEDIA_PLAN_CONFIG[planLevel]?.fieldColumn ||
            GENERATE_MEDIA_PLAN_CONFIG["Default"].fieldColumn;
        const planRecord = params.data[editedPlanRecordKey];
        return (
            mediaPlanEditConfig[planLevel]?.[planRecord]?.includes(
                params.colDef.field
            ) ?? false
        );
    };

    if (params.node.rowPinned)
        return numericValueFormatter(params as ValueFormatterParams);

    if (isEditing && (isMediaPlanEdit || isMediaPlanCreate)) {
        return (
            <Input
                type="number"
                ref={inputRef}
                defaultValue={params.value}
                onChange={(e) => setInputValue(Number(e.target.value))}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                style={{ height: "100%" }}
            />
        );
    }
    return (
        <div
            ref={cellRef}
            onDoubleClick={handleDoubleClick}
            style={{ height: "100%", padding: "4px" }}
            className={
                isModifiedCell() && !params.node.rowPinned
                    ? "modified-planning-grid-cell"
                    : isMediaPlanEdit || isMediaPlanCreate
                        ? "default-planning-grid-cell"
                    : ""
            }
        >
            <div
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        params.node.setDataValue(
                            params.colDef.field,
                            originalValue
                        );
                        params.api.refreshCells({
                            rowNodes: [params.node],
                            force: true,
                        });
                    }
                }}
                className={
                    isModifiedCell()
                        ? "modified-planning-grid-cell"
                        : !isFinite(params.value)
                            ? "invalid-planning-grid-cell"
                        : isMediaPlanEdit || isMediaPlanCreate
                                ? "default-planning-grid-cell"
                                : ""
                }
            >
                {numericValueFormatter(params as ValueFormatterParams)}
            </div>
        </div>
    );
};

export default PlanRatioCellRenderer;
